// Conversation state for Misti's face - a pure reducer, no React, no timers.
//
// The face has to tell a guest, from three metres away and without words, which
// of these is true right now:
//
//   she is idle ............ nobody has called her
//   she is listening ....... she heard her name and is waiting for you
//   she is hearing you ..... you are mid-sentence and it is going in
//   she is thinking ........ you have finished and the answer is coming
//   she is working ......... she is looking something up (menu, order)
//   she is speaking ........ the answer
//
// Before this, the face showed none of it. The 2-4 s between a guest finishing
// and Misti answering looked identical to "she didn't hear me", which is when
// people repeat themselves and talk over her reply.
//
// Everything that happens is an event: reduce(model, event, now) returns the next
// model. Time-based transitions (drowsiness, safety timeouts, debounces) happen in
// tick(model, now), which the render loop calls every frame. Keeping both pure is
// what makes the stuck-state and race tests in stateMachine.test.js possible.

export const STATES = [
	"asleep", // long idle: eyes shut, slow breathing
	"drowsy", // idle a while: heavy lids, slow blinks
	"idle", // awake and ambient: blinking, glancing about
	"waking", // brief bridge out of asleep/drowsy when her name is called
	"listening", // mic open, waiting for the guest
	"hearing", // the guest is mid-sentence (interim transcripts arriving)
	"thinking", // the guest finished; the reply has not started yet
	"working", // a tool call is running - looking something up
	"speaking", // her audio is playing
	"error", // the pipeline failed
	"reconnecting", // the call dropped and is being re-established
];

// All in milliseconds.
export const T = {
	// Late on purpose. BellaBot's sleeping face is much loved, but a robot that
	// looks asleep also looks unavailable, and on shift that costs guests. So she
	// only gets drowsy after two quiet minutes and only sleeps after ten - a
	// real lull, not a pause between tables.
	IDLE_TO_DROWSY: 120_000,
	DROWSY_TO_ASLEEP: 480_000,
	WAKING: 700,
	// Interim transcripts arrive in bursts; without a hold, hearing<->listening
	// would strobe between every word.
	HEARING_HOLD: 1_200,
	// rtf-bot-stopped-speaking fires once per utterance CHUNK, not once per
	// turn, so a multi-sentence reply produces stop/start pairs between
	// sentences. Leaving `speaking` on the first stop would snap the face to
	// listening and back several times per answer. Gaps between TTS chunks run
	// past 350 ms often enough that 700 is the safe floor; it also gives her a
	// natural beat, mouth closed, before she turns back to listening.
	SPEAKING_TAIL: 700,
	// Safety exits. Every non-resting state has one, so a lost event can never
	// strand the face - a reply that never comes, a stop-speaking that is
	// dropped, a tool call whose end is missed.
	THINKING_MAX: 15_000,
	WORKING_MAX: 25_000,
	SPEAKING_MAX: 60_000,
	HEARING_MAX: 20_000,
	ERROR_SHOW: 4_000,
	RECONNECTING_MAX: 30_000,
};

const RESTING = new Set(["asleep", "drowsy", "idle"]);
const IN_CONVERSATION = new Set(["listening", "hearing", "thinking", "working", "speaking"]);

export function initialModel(now = 0) {
	return {
		state: "asleep", // behind the play button she is asleep
		since: now,
		started: false,
		micOpen: false,
		callStatus: "idle",
		wasConnected: false,
		lastActivity: now,
		lastHeard: 0,
		speakingEndsAt: 0, // pending exit from `speaking`, see SPEAKING_TAIL
		toolRunning: false,
	};
}

function go(model, state, now) {
	if (model.state === state) return model;
	return { ...model, state, since: now, speakingEndsAt: 0 };
}

// Where she settles when nothing conversational is happening.
function rest(model, now) {
	if (model.callStatus === "failed") return go(model, "error", now);
	return go(model, model.micOpen ? "listening" : "idle", now);
}

export function reduce(model, event, now) {
	const m = { ...model };

	switch (event.type) {
		case "START": {
			m.started = true;
			m.lastActivity = now;
			return go(m, "waking", now);
		}

		case "CALL_STATUS": {
			const s = event.status;
			m.callStatus = s;
			if (s === "connected") {
				m.wasConnected = true;
				if (m.state === "reconnecting" || m.state === "error") return rest(m, now);
				return m;
			}
			if (s === "failed") return go(m, "error", now);
			// connecting / idle. The first connect at power-on is not worth showing;
			// a drop in the middle of a conversation is.
			if (m.wasConnected && (IN_CONVERSATION.has(m.state) || m.state === "error")) {
				return go(m, "reconnecting", now);
			}
			return m;
		}

		case "WAKE": {
			m.lastActivity = now;
			m.micOpen = true;
			// From sleep there is a visible waking beat - otherwise she snaps from
			// eyes-shut to wide-open, which reads as a jump-scare.
			if (m.state === "asleep" || m.state === "drowsy") return go(m, "waking", now);
			if (m.state === "speaking" || m.state === "thinking" || m.state === "working") return m;
			return go(m, "listening", now);
		}

		case "MIC": {
			m.micOpen = !!event.open;
			if (event.open) {
				m.lastActivity = now;
				if (RESTING.has(m.state)) return go(m, "listening", now);
				return m;
			}
			// Closing the mic ends listening/hearing. It does NOT cut her off
			// mid-sentence or abandon a reply that is on its way.
			if (m.state === "listening" || m.state === "hearing") return go(m, "idle", now);
			return m;
		}

		case "USER_SPEAKING": {
			m.lastActivity = now;
			m.lastHeard = now;
			// While she is speaking this is usually her own voice leaking into the
			// mic, or the guest talking over her; either way the reply is still
			// playing, so the face keeps showing that.
			if (m.state === "speaking") return m;
			return go(m, "hearing", now);
		}

		case "USER_FINAL": {
			m.lastActivity = now;
			m.lastHeard = now;
			if (m.state === "speaking") return m;
			if (m.state === "working") return m;
			return go(m, "thinking", now);
		}

		case "TOOL": {
			m.lastActivity = now;
			m.toolRunning = !!event.started;
			if (event.started) {
				if (m.state === "speaking") return m;
				return go(m, "working", now);
			}
			if (m.state === "working") return go(m, "thinking", now);
			return m;
		}

		case "BOT_SPEAKING": {
			m.lastActivity = now;
			if (event.speaking) {
				// Cancels a pending tail exit - this is the next sentence of the same
				// reply, not a new turn.
				return { ...go(m, "speaking", now), speakingEndsAt: 0 };
			}
			if (m.state !== "speaking") return m;
			return { ...m, speakingEndsAt: now + T.SPEAKING_TAIL };
		}

		case "ERROR": {
			return go(m, "error", now);
		}

		case "POKE":
		case "EMOTION": {
			// Any sign of life that is not conversational (a tap on the screen,
			// a teleop message, an emotion sent from the app) - just resets the
			// drowsiness clock, and wakes her so the emotion can show.
			m.lastActivity = now;
			if (m.state === "drowsy" || m.state === "asleep") return go(m, "waking", now);
			return m;
		}

		default:
			return m;
	}
}

export function tick(model, now) {
	const m = model;
	const age = now - m.since;

	switch (m.state) {
		case "idle":
			if (m.started && now - m.lastActivity > T.IDLE_TO_DROWSY) return go(m, "drowsy", now);
			return m;

		case "drowsy":
			if (now - m.lastActivity > T.IDLE_TO_DROWSY + T.DROWSY_TO_ASLEEP) return go(m, "asleep", now);
			return m;

		case "waking":
			if (age > T.WAKING) return rest(m, now);
			return m;

		case "hearing":
			// The final transcript normally moves us to `thinking`. If it never
			// comes, fall back once the interim stream has gone quiet.
			if (now - m.lastHeard > T.HEARING_HOLD && age > T.HEARING_HOLD) return rest(m, now);
			if (age > T.HEARING_MAX) return rest(m, now);
			return m;

		case "thinking":
			if (age > T.THINKING_MAX) return rest(m, now);
			return m;

		case "working":
			if (age > T.WORKING_MAX) return go(m, "thinking", now);
			return m;

		case "speaking":
			if (m.speakingEndsAt && now >= m.speakingEndsAt) return rest(m, now);
			if (age > T.SPEAKING_MAX) return rest(m, now);
			return m;

		case "error":
			if (age > T.ERROR_SHOW) {
				if (m.callStatus === "connected") return rest(m, now);
				// Still failing. The app retries on its own, so show that it is
				// trying rather than a permanent sad face.
				return go(m, m.wasConnected ? "reconnecting" : "idle", now);
			}
			return m;

		case "reconnecting":
			if (m.callStatus === "connected") return rest(m, now);
			if (age > T.RECONNECTING_MAX) return go(m, "error", now);
			return m;

		default:
			return m;
	}
}
